import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { trackAdminAction } from '@/lib/analytics';
import { Truck, Plus, Wrench, Edit2, Trash2, Settings, Building2, Sun, Zap, Wind, Wifi, MapPin, QrCode, ChevronUp, ChevronDown, Circle, DollarSign, Link2, Link2Off, AlertCircle, AlertTriangle, Search, Check, Loader2, ClipboardList } from 'lucide-react';
import { BulkAssignDrawer } from '@/components/units/bulkAssign/BulkAssignDrawer';
import { DevicePickerDrawer } from '@/components/settings/DevicePickerDrawer';
import type { DeviceType } from '@/hooks/useDeviceLinking';
import { SystemStatusIcons } from '@/components/unit/SystemStatusIcons';
import { getSystemIconColor } from '@/components/unit/SystemStatusIcons.helpers';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { connectionStatusVariant } from '@/lib/connectivityStatus';
import { AdminDataTable, Column, Action, BulkAction } from '@/components/settings/AdminDataTable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { SettingsFilterBar } from '@/components/settings/SettingsFilterBar';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAdminUnits, createAdminUnit, updateAdminUnit, deleteAdminUnit, getAdminGroups, getAdminCompanys, checkUnitCodeExists } from '@/lib/adminApi';
import type { AdminUnitSystems } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { describeUnitCreated } from '@/lib/autoLinkSummary';
import { useLabels } from '@/hooks/useLabels';
import { useHierarchicalSearch } from '@/hooks/useHierarchicalSearch';
import { useFilters } from '@/contexts/FilterContext';
import { useSettingsFilters } from '@/contexts/SettingsFilterContext';
import { useUnitStatus } from '@/contexts/UnitStatusContext';
import { useRBAC } from '@/contexts/RBACContext';
import { MODEL_LABELS, INDUSTRY_TYPE_LABELS, INDUSTRY_TYPE_ORDER, MODEL_TYPE_MAPPING, ORIGIN_LABELS, ORIGIN_GROUPS } from '@/lib/constants';
import { BulkRateAssignmentModal } from '@/components/settings/BulkRateAssignmentModal';
import { EntityRateAssignmentSection } from '@/components/settings/EntityRateAssignmentSection';
import { EntityAuditTimeline } from '@/components/settings/EntityAuditTimeline';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import {
  useNewDevices,
  type NewDevice,
  type UnitSuggestion,
  type BulkApprovalPreview,
} from '@/hooks/useNewDevices';
import { NewDevicesTable } from '@/components/settings/NewDevicesTable';
import { authenticatedFetch } from '@/lib/api';
import type { BulkRateAssignmentRequest, BulkRateAssignmentResponse } from '@/types/rates';
import type { IndustryType, UnitFilterData, Unit, Group, TrailerSystems, StarlinkSystem } from '@/types';

// System status from linked devices (matches map sidebar display). The shape
// is owned by the admin units API boundary (`AdminUnitSystems` in adminApi.ts,
// ISS-021/022 for the starlink range); aliased here so existing references keep
// their name while the definition stays single-sourced (ISS-031).
type SystemStatus = AdminUnitSystems;

/** Admin units table row shape (API + mapped fields); distinct from fleet map `Unit` in @/types */
interface SettingsUnitRow {
  id: string;
  unit_code: string;
  unit_id: string;
  vin: string;
  status: 'active' | 'inactive' | 'maintenance' | 'off_rent';
  model?: string;
  model_code?: string;
  industry_type?: IndustryType;
  site_name?: string | null; // Direct group name (child/site level)
  group_name?: string;       // Parent group name (group level)
  company_name?: string;
  company_id?: string;
  primary_group_id?: string;
  last_seen?: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state_province?: string;
  country?: string;
  // System status for UI display (from linked devices)
  systems?: SystemStatus;
  first_seen_at?: string;
  last_comms_at?: string;
}

/** Minimal company shape used by the unit form/company dropdowns. */
interface UnitCompanyOption {
  id: string;
  name: string;
  industry?: string;
}

/**
 * Sol-Ark column cell for the fleet table: plant name over the plant ID line.
 * Appends the physical-inverter count to the ID line ONLY when a plant holds
 * more than one physical inverter (WEA001TCA case). For absent/1 the output is
 * byte-identical to the plain 'Plant {externalId}' line — the count is absent
 * until the first full sync populates last_status.inverter_count.
 * Exported for tests.
 */
export function SolArkPlantCell({ solar }: { solar: SystemStatus['solar'] }) {
  if (!solar?.enabled) return <span className="text-muted-foreground">—</span>;
  const physicalCount = solar.physicalInverterCount;
  const showCount = typeof physicalCount === 'number' && physicalCount > 1;
  return (
    <div className="min-w-0" title={[solar.name, solar.externalId].filter(Boolean).join(' · ')}>
      <span className="block truncate text-xs font-medium text-foreground">
        {solar.name || 'Unnamed plant'}
      </span>
      {solar.externalId && (
        <span className="block truncate font-mono text-[10px] text-muted-foreground">
          Plant {solar.externalId}
          {showCount ? ` · ${physicalCount} inverters` : null}
        </span>
      )}
    </div>
  );
}

function normalizeHvacMode(mode: string | undefined): 'cool' | 'heat' | 'fan' | 'off' {
  const m = (mode || 'off').toLowerCase();
  if (m === 'cool' || m === 'heat' || m === 'fan' || m === 'off') return m;
  return 'off';
}

function normalizeChargerStatus(status: string | undefined): 'charging' | 'idle' | 'disconnected' {
  const s = (status || 'idle').toLowerCase();
  if (s === 'charging' || s === 'idle' || s === 'disconnected') return s;
  return 'idle';
}

function buildStarlinkFromSystemStatus(s: SystemStatus['starlink']): StarlinkSystem | undefined {
  if (!s) return undefined;
  const ts = new Date().toISOString();
  return {
    enabled: s.enabled,
    connectionStatus: s.connectionStatus,
    signalStrength: 0,
    signalQuality: 0,
    noiseLevel: 0,
    currentMetrics: {
      downloadSpeed: 0,
      uploadSpeed: 0,
      pingLatency: 0,
      packetLoss: 0,
      timestamp: ts,
    },
    metricsHistory: [],
    totalClients: 0,
    wifiClients: [],
    clients2_4GHz: 0,
    clients5GHz: 0,
    terminalId: s.externalId ?? '',
    firmwareVersion: '',
    hardwareVersion: '',
    uptime: 0,
    lastReboot: ts,
    ssid: '',
    channel2_4GHz: 0,
    channel5GHz: 0,
    wifiEnabled: false,
  };
}

/** Maps admin settings row + SystemStatus into fleet `Unit` for SystemStatusIcons / resolveSystemMetrics */
function settingsUnitRowToFleetUnit(row: SettingsUnitRow): Unit {
  const systems = row.systems;
  let trailerSystems: TrailerSystems | undefined;
  if (systems) {
    const starlink = buildStarlinkFromSystemStatus(systems.starlink);
    trailerSystems = {
      solar: {
        enabled: systems.solar?.enabled ?? false,
        battery: systems.solar?.battery ?? 0,
        generation: systems.solar?.generation ?? 0,
        consumption: 0,
        status: systems.solar?.isCharging ? 'charging' : 'idle',
      },
      charger: {
        enabled: systems.charger?.enabled ?? false,
        status: normalizeChargerStatus(systems.charger?.status),
        powerIn: 0,
        powerOut: normalizeChargerStatus(systems.charger?.status) === 'charging' ? 1 : 0,
      },
      hvac: {
        enabled: systems.hvac?.enabled ?? false,
        temperature: 0,
        targetTemp: 0,
        mode: normalizeHvacMode(systems.hvac?.mode),
        fanSpeed: 'med',
      },
      ...(starlink ? { starlink } : {}),
    };
  }

  return {
    id: row.id,
    unit_code: row.unit_code,
    name: row.name ?? row.unit_code,
    starlinkId: '',
    status: 'unknown',
    location: {
      lat: row.latitude ?? 0,
      lng: row.longitude ?? 0,
      address: [row.city, row.state_province].filter(Boolean).join(', ') || '',
    },
    image: '',
    lastUpdate: row.last_comms_at ?? row.last_seen ?? '',
    company_id: row.company_id ?? '',
    primary_group_id: row.primary_group_id ?? null,
    industry_type: row.industry_type,
    systems: trailerSystems,
  };
}

/**
 * Render the Unit Management settings UI for viewing and managing trailer units.
 *
 * Loads units, groups, and companies from the admin API and presents a searchable table with per-row actions
 * (edit, delete) and bulk actions (bulk edit metadata, set maintenance, set active). Provides drawers for
 * single-unit editing and bulk updates, and handles mapping API responses to the component's unit shape.
 *
 * @returns The React element for the Unit Management settings page, including the search bar, data table, and edit drawers.
 */
export function SettingsUnits() {
  const { getLabel } = useLabels();

  // Get user's accessible companies to determine available industries
  const { companies: userCompanies, isSuperadmin } = useRBAC();

  // Derive user's accessible industries from their companies
  const accessibleIndustries = useMemo(() => {
    // Superadmins can see all industries
    if (isSuperadmin) {
      return INDUSTRY_TYPE_ORDER;
    }
    // Get unique industries from user's accessible companies
    const industries = new Set<IndustryType>();
    userCompanies.forEach(company => {
      if (company.industry) {
        industries.add(company.industry);
      }
    });
    // Return in standard order, filtered to only accessible industries
    return INDUSTRY_TYPE_ORDER.filter(type => industries.has(type));
  }, [userCompanies, isSuperadmin]);

  // Whether user has access to multiple industries (to show/hide industry type dropdown)
  const hasMultipleIndustries = accessibleIndustries.length > 1;

  // Filter models to only show those applicable to user's accessible industries
  const accessibleModels = useMemo(() => {
    return Object.entries(MODEL_LABELS).filter(([code]) => {
      const modelIndustries = MODEL_TYPE_MAPPING[code] || [];
      // Model is accessible if ANY of its industries match user's accessible industries
      return modelIndustries.some(industry => accessibleIndustries.includes(industry));
    });
  }, [accessibleIndustries]);

  // Settings-specific filters (independent from Map page)
  const {
    selectedIndustries,
    selectedCompanyIds: settingsCompanyIds,
    isLoading: filtersLoading,
  } = useSettingsFilters();

  // Use FilterContext for consistent filtering across pages
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedIndustryTypes,
    selectedModels,
    selectedStatuses,
    setUnits: setContextUnits,
    refreshFleetData
  } = useFilters();

  // Real-time status from shared polling context
  const { getStatus } = useUnitStatus();

  const [units, setUnits] = useState<SettingsUnitRow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [companies, setCompanies] = useState<UnitCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Use hierarchical search for company/group expansion
  const { matchesHierarchy } = useHierarchicalSearch(searchTerm);
  const [activeIndustryTab, setActiveIndustryTab] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<SettingsUnitRow | null>(null);
  const [selectedUnits, setSelectedUnits] = useState<SettingsUnitRow[]>([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignPrefill, setBulkAssignPrefill] = useState<string[]>([]);
  /** Unit IDs currently saving status from the inline Status Select (per-row loading) */
  const [updatingUnitIds, setUpdatingUnitIds] = useState<Set<string>>(() => new Set());
  const [formData, setFormData] = useState({
    unit_code: '',
    unit_model: '',      // Model portion of unit code (e.g., '1RE')
    unit_number: '',     // Number portion of unit code (e.g., '001')
    unit_origin: '',     // Origin portion of unit code (e.g., 'TCA')
    name: '',
    vin: '',
    status: 'active',
    industry_type: '',
    company_id: '',
    group_id: '',       // Parent group (group level)
    site_id: '',        // Child group (site level) - the actual primary_group_id
    latitude: '',
    longitude: '',
  });

  // Global duplicate check state (checks across ALL companies, bypasses RBAC)
  const [isCheckingGlobal, setIsCheckingGlobal] = useState(false);
  const [isGlobalDuplicate, setIsGlobalDuplicate] = useState<boolean | null>(null);

  // Bulk rate assignment state
  const [bulkRateModalOpen, setBulkRateModalOpen] = useState(false);
  const [unitsForRateAssignment, setUnitsForRateAssignment] = useState<SettingsUnitRow[]>([]);
  const [isSubmittingRate, setIsSubmittingRate] = useState(false);
  const { rates, isLoading: isLoadingRates } = useEnergyRates({ autoFetch: true });

  // New devices (unmatched from Starlink/Sol-Ark)
  const {
    devices: newDevices,
    totalCount: newDeviceTotalCount,
    counts: newDeviceCounts,
    isLoading: isLoadingNewDevices,
    isSubmitting: isSubmittingNewDevice,
    isBulkApproving,
    linkDevice,
    dismissDevice,
    markCreated: markDeviceCreated,
    getSuggestions,
    getBulkApprovalPreview,
    approvePending,
  } = useNewDevices({ autoFetch: true });

  // State for new device actions
  const [selectedNewDevice, setSelectedNewDevice] = useState<NewDevice | null>(null);
  const [linkDrawerOpen, setLinkDrawerOpen] = useState(false);
  const [unitSuggestions, setUnitSuggestions] = useState<UnitSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState<Array<{ id: string; unit_code: string; name: string | null }>>([]);
  const [isSearchingUnits, setIsSearchingUnits] = useState(false);
  const [selectedUnitForLink, setSelectedUnitForLink] = useState<{ id: string; unit_code: string } | null>(null);
  const [linkGroupId, setLinkGroupId] = useState<string>('');
  const [linkSiteId, setLinkSiteId] = useState<string>('');

  // Bulk pending-unit approval state
  const [bulkApprovalOpen, setBulkApprovalOpen] = useState(false);
  const [bulkApprovalPreview, setBulkApprovalPreview] = useState<BulkApprovalPreview | null>(null);
  const [isLoadingBulkPreview, setIsLoadingBulkPreview] = useState(false);
  const [bulkApprovalGroupId, setBulkApprovalGroupId] = useState('');
  const [bulkApprovalSiteId, setBulkApprovalSiteId] = useState('');

  // Device unlink state (superadmin only)
  const [isUnlinkingStarlink, setIsUnlinkingStarlink] = useState(false);
  const [isUnlinkingSolark, setIsUnlinkingSolark] = useState(false);
  const [isUnlinkingOmni, setIsUnlinkingOmni] = useState(false);

  // GPS discovery state (superadmin only)

  // Device picker drawer state (superadmin only)
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [devicePickerType, setDevicePickerType] = useState<DeviceType>('starlink');

  const openDevicePicker = (type: DeviceType) => {
    setDevicePickerType(type);
    setDevicePickerOpen(true);
  };

  const handleDeviceLinked = () => {
    reloadAfterFleetMutation();
  };

  // ============================================
  // Unit Code Generator: Parse and Generate Functions
  // ============================================

  /**
   * Parse a unit code into its components: model, number, origin
   * Format: [MODEL_CODE (3 chars)][UNIT_NUMBER (3 digits)][ORIGIN_CODE (2-4+ chars)]
   * Examples: 1RE001TCA, HNY072TNYC, 2RE015TUK
   */
  const parseUnitCode = (code: string): { model: string; number: string; origin: string } | null => {
    if (!code || code.length < 7) return null;

    // Match pattern: 3 alphanumeric chars + 3 digits + remaining chars
    const match = code.toUpperCase().match(/^([A-Z0-9]{3})(\d{3})(.+)$/);
    if (!match) return null;

    const [, model, number, origin] = match;

    // Validate model exists in MODEL_LABELS
    if (!MODEL_LABELS[model]) return null;

    // Validate origin exists in ORIGIN_LABELS
    if (!ORIGIN_LABELS[origin]) return null;

    return { model, number, origin };
  };

  /**
   * Generate unit code from components
   * Pads number to 3 digits (e.g., "1" becomes "001")
   */
  const generateUnitCode = (model: string, number: string, origin: string): string => {
    if (!model || !number || !origin) return '';
    const paddedNumber = number.replace(/\D/g, '').padStart(3, '0').slice(0, 3);
    return `${model}${paddedNumber}${origin}`;
  };

  /**
   * Get all used unit numbers for a given model+origin combination
   * Returns a Set of numbers (as integers) that are already in use
   */
  const getUsedNumbers = (model: string, origin: string): Set<number> => {
    const used = new Set<number>();
    if (!model || !origin) return used;

    units.forEach(unit => {
      // When editing, exclude the current unit
      if (editingUnit && unit.id === editingUnit.id) return;

      const parsed = parseUnitCode(unit.unit_code || '');
      if (parsed && parsed.model === model && parsed.origin === origin) {
        used.add(parseInt(parsed.number, 10));
      }
    });

    return used;
  };

  /**
   * Find the next available unit number for a model+origin combination
   * Finds the lowest unused number starting from 1
   */
  const getNextAvailableNumber = (model: string, origin: string): string => {
    const used = getUsedNumbers(model, origin);
    for (let i = 1; i <= 999; i++) {
      if (!used.has(i)) {
        return i.toString().padStart(3, '0');
      }
    }
    return '999'; // Fallback if all numbers are taken
  };

  /**
   * Check if a specific number is available for the given model+origin
   */
  const isNumberAvailable = (model: string, origin: string, number: string): boolean => {
    if (!model || !origin || !number) return true;
    const used = getUsedNumbers(model, origin);
    const num = parseInt(number.padStart(3, '0'), 10);
    return !used.has(num);
  };

  /**
   * Find the next available number going up from current
   */
  const getNextNumberUp = (model: string, origin: string, current: string): string => {
    const used = getUsedNumbers(model, origin);
    const currentNum = parseInt(current.padStart(3, '0'), 10) || 0;

    for (let i = currentNum + 1; i <= 999; i++) {
      if (!used.has(i)) {
        return i.toString().padStart(3, '0');
      }
    }
    // If no available number going up, return current
    return current.padStart(3, '0');
  };

  /**
   * Find the next available number going down from current
   */
  const getNextNumberDown = (model: string, origin: string, current: string): string => {
    const used = getUsedNumbers(model, origin);
    const currentNum = parseInt(current.padStart(3, '0'), 10) || 1;

    for (let i = currentNum - 1; i >= 1; i--) {
      if (!used.has(i)) {
        return i.toString().padStart(3, '0');
      }
    }
    // If no available number going down, return current
    return current.padStart(3, '0');
  };

  // ============================================
  // Unit Code Generator: Bidirectional Change Handlers
  // ============================================

  /**
   * When user types in the full unit code field - parse and fill components
   */
  const handleUnitCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value.toUpperCase();
    const parsed = parseUnitCode(code);

    if (parsed) {
      // Valid code - sync all fields
      setFormData(prev => ({
        ...prev,
        unit_code: code,
        unit_model: parsed.model,
        unit_number: parsed.number,
        unit_origin: parsed.origin,
      }));
    } else {
      // Invalid/incomplete - just update the code, don't clear components
      setFormData(prev => ({ ...prev, unit_code: code }));
    }
  };

  /**
   * When user selects a model from dropdown
   * Auto-suggests next available number if current number is taken or empty
   */
  const handleModelChange = (model: string) => {
    setFormData(prev => {
      let newNumber = prev.unit_number;

      // If origin is set, check if we need to suggest a new number
      if (prev.unit_origin) {
        // If no number set, or current number is taken, suggest next available
        if (!prev.unit_number || !isNumberAvailable(model, prev.unit_origin, prev.unit_number)) {
          newNumber = getNextAvailableNumber(model, prev.unit_origin);
        }
      }

      const newCode = generateUnitCode(model, newNumber, prev.unit_origin);
      return { ...prev, unit_model: model, unit_number: newNumber, unit_code: newCode };
    });
  };

  /**
   * When user types a unit number
   */
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3); // Numbers only, max 3
    setFormData(prev => {
      // Generate code with padded number
      const paddedValue = value.padStart(3, '0');
      const newCode = generateUnitCode(prev.unit_model, paddedValue, prev.unit_origin);
      return { ...prev, unit_number: value, unit_code: newCode };
    });
  };

  /**
   * When user selects an origin from dropdown
   * Auto-suggests next available number if current number is taken or empty
   */
  const handleOriginChange = (origin: string) => {
    setFormData(prev => {
      let newNumber = prev.unit_number;

      // If model is set, check if we need to suggest a new number
      if (prev.unit_model) {
        // If no number set, or current number is taken, suggest next available
        if (!prev.unit_number || !isNumberAvailable(prev.unit_model, origin, prev.unit_number)) {
          newNumber = getNextAvailableNumber(prev.unit_model, origin);
        }
      }

      const newCode = generateUnitCode(prev.unit_model, newNumber, origin);
      return { ...prev, unit_origin: origin, unit_number: newNumber, unit_code: newCode };
    });
  };

  /**
   * Increment unit number to next available
   */
  const handleNumberIncrement = () => {
    setFormData(prev => {
      const nextNum = getNextNumberUp(prev.unit_model, prev.unit_origin, prev.unit_number || '000');
      const newCode = generateUnitCode(prev.unit_model, nextNum, prev.unit_origin);
      return { ...prev, unit_number: nextNum, unit_code: newCode };
    });
  };

  /**
   * Decrement unit number to previous available
   */
  const handleNumberDecrement = () => {
    setFormData(prev => {
      const prevNum = getNextNumberDown(prev.unit_model, prev.unit_origin, prev.unit_number || '002');
      const newCode = generateUnitCode(prev.unit_model, prevNum, prev.unit_origin);
      return { ...prev, unit_number: prevNum, unit_code: newCode };
    });
  };

  // Local duplicate check (fast, checks against RBAC-filtered units)
  const isLocalDuplicate = useMemo(() => {
    const code = formData.unit_code.trim();
    if (!code) return false;

    // When editing, exclude the current unit from the check
    const existingCodes = units
      .filter(u => editingUnit ? u.id !== editingUnit.id : true)
      .map(u => u.unit_code.toLowerCase());

    return existingCodes.includes(code.toLowerCase());
  }, [formData.unit_code, units, editingUnit]);

  // Combined duplicate check (local OR global)
  const isDuplicateUnitCode = useMemo(() => {
    if (isLocalDuplicate) return true;
    return isGlobalDuplicate === true;
  }, [isLocalDuplicate, isGlobalDuplicate]);

  // Debounced global duplicate check (queries entire database, bypasses RBAC)
  useEffect(() => {
    const code = formData.unit_code.trim();

    // Skip if empty or too short (minimum valid code length is 7: 3 model + 3 number + 1+ origin)
    if (code.length < 7) {
      setIsGlobalDuplicate(null);
      return;
    }

    // Skip global check if already found locally (save API call)
    if (isLocalDuplicate) {
      setIsGlobalDuplicate(null);
      return;
    }

    setIsCheckingGlobal(true);

    const timeoutId = setTimeout(async () => {
      try {
        const exists = await checkUnitCodeExists(code, editingUnit?.id);
        setIsGlobalDuplicate(exists);
      } catch (error) {
        console.error('Global duplicate check failed:', error);
        // On error, allow form submission - DB constraint will catch conflicts
        setIsGlobalDuplicate(null);
      } finally {
        setIsCheckingGlobal(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.unit_code, editingUnit?.id, isLocalDuplicate]);

  // Reload data when settings filters change - wait for context to initialize first
  // Use JSON.stringify for stable dependency on array contents
  const selectedIndustriesKey = JSON.stringify(selectedIndustries);
  const settingsCompanyIdsKey = JSON.stringify(settingsCompanyIds);
  useEffect(() => {
    // Don't load until filters are initialized to prevent double-loading
    if (filtersLoading) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustriesKey, settingsCompanyIdsKey, filtersLoading]);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [unitsData, groupsData, companiesData] = await Promise.all([
        getAdminUnits({ industries: selectedIndustries }),
        getAdminGroups({ industries: selectedIndustries }),
        getAdminCompanys({ industries: selectedIndustries }),
      ]);

      console.log('[Units Page] API returned:', {
        unitsCount: unitsData.units?.length || 0,
        url: window.location.href
      });

      // Build a map of groups for quick lookup
      const groupsMap = new Map((groupsData.groups || []).map((g): [string, Group] => [g.id, g]));

      // Map API response to component interface
      const mappedUnits = unitsData.units.map((u) => {
        // Get the direct group (site level)
        const directGroup = groupsMap.get(u.primary_group_id!);
        // Get the parent group (group level) if direct group has a parent
        const parentGroup = directGroup?.parent_group_id
          ? groupsMap.get(directGroup.parent_group_id)
          : null;

        // If direct group has a parent, it's a site - show parent as group_name
        // If direct group has no parent, it's the root group itself
        const siteName = directGroup?.parent_group_id
          ? (directGroup?.name || u.primary_group?.name || 'Unassigned')
          : null;
        const groupName = parentGroup?.name || (directGroup?.parent_group_id ? 'Unknown' : (directGroup?.name || u.primary_group?.name || 'Unassigned'));

        return {
          id: u.id,
          unit_code: u.unit_code || u.id,
          unit_id: u.unit_code || u.id,
          // Keep the real VIN ('' when unset) in the model — never a display
          // sentinel. The edit form seeds from this and now persists it, so a
          // '-' here would round-trip into the DB as a literal VIN (Sentry HIGH,
          // PR #729). There is no VIN table column, so nothing displays it.
          vin: u.vin || '',
          status: u.status || 'active',
          model: u.model_code || u.model || '-',
          model_code: u.model_code,
          // Preserve the raw industry (undefined when unset) — the grouping and
          // filter sites re-default to 'film' locally, but the edit form seeds
          // from this and now persists it, so a baked-in 'film' here would
          // silently rewrite a genuinely null industry on any unrelated edit
          // (Codex P2, PR #729 — same class as the VIN sentinel above).
          industry_type: u.industry_type ?? undefined,
          site_name: siteName,
          group_name: groupName,
          company_name: u.companies?.name || u.company?.name || 'Unknown',
          company_id: u.company_id,
          primary_group_id: u.primary_group_id,
          last_seen: u.last_seen_at || u.lastUpdate || u.updated_at,
          name: u.name,
          latitude: u.latitude,
          longitude: u.longitude,
          city: u.city,
          state_province: u.state_province,
          country: u.country,
          systems: u.systems,
          first_seen_at: u.first_seen_at || u.created_at,
          last_comms_at: u.last_comms_at,
        };
      });

      console.log('[Units Page] Mapped units sample:', mappedUnits.slice(0, 2));

      setUnits(mappedUnits);
      setGroups(groupsData.groups || []);
      setCompanies(companiesData.companies || []);
    } catch (error) {
      console.error('Failed to load units:', error);
      toast.error('Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reload after a write that changed fleet structure (assignment, create, delete).
   *
   * This page reads /admin/units, which is uncached, so loadData() alone makes the
   * change look applied HERE while the Map, the filter sidebar and Reports keep
   * serving their cached pre-mutation fleet — the exact split an operator reports
   * as "Settings shows the new production but the map doesn't". refreshFleetData()
   * drops those caches and re-runs their loaders.
   */
  const reloadAfterFleetMutation = async () => {
    await loadData(true);
    await refreshFleetData();
  };

  // Push units to FilterContext so filter dropdowns can calculate counts
  // NOTE: We intentionally do NOT include getStatus in this effect or the unitsForContext.
  // Status is polled every 15s and including it here would cause excessive re-renders.
  // Filter components should call getStatus(unit.id) directly for live status display.
  useEffect(() => {
    if (units.length > 0) {
      // Convert SettingsUnitRow[] to UnitFilterData[] for FilterContext (static structure only)
      // Status is set to 'unknown' placeholder - filters call getStatus() directly for live data
      const unitsForContext: UnitFilterData[] = units.map(u => ({
        id: u.id,
        unit_code: u.unit_code,
        industry_type: u.industry_type || 'film',
        company_id: u.company_id || '',
        primary_group_id: u.primary_group_id || null,
        status: 'unknown', // Placeholder - filters use getStatus() for live status
      }));
      setContextUnits(unitsForContext);
    }
  }, [units, setContextUnits]);

  // Apply all FilterContext filters + search to units, respecting RBAC industry access
  const filteredUnits = useMemo(() => {
    console.log('[Units Page] Filtering with FilterContext:', {
      totalUnits: units.length,
      searchTerm,
      selectedIndustryTypes: selectedIndustryTypes.length,
      selectedModels: selectedModels.length,
      selectedStatuses: selectedStatuses.length,
      selectedCompanyIds: selectedCompanyIds.length,
      selectedGroupIds: selectedGroupIds.length,
      accessibleIndustries: accessibleIndustries.length
    });

    // Note: We don't return empty when selectedIndustryTypes is empty because:
    // 1. FilterContext may not be initialized on settings pages
    // 2. The API call already filters by industry via SettingsFilterContext
    // 3. RBAC filtering below (accessibleIndustries) handles the access control

    const filtered = units.filter((unit) => {
      // RBAC: Only show units from industries user has access to
      const unitIndustryType: IndustryType = unit.industry_type || 'film';
      if (!accessibleIndustries.includes(unitIndustryType)) {
        return false;
      }

      // Search filter - combines hierarchical matching with direct string matching
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        // Hierarchical matching: searching "NBC Universal" shows all units under that company/group
        const matchesHierarchyResult = matchesHierarchy(unit);
        // Direct string matching for unit-specific fields
        const matchesDirectSearch =
          unit.unit_id?.toLowerCase().includes(searchLower) ||
          unit.vin?.toLowerCase().includes(searchLower) ||
          unit.name?.toLowerCase().includes(searchLower) ||
          unit.company_name?.toLowerCase().includes(searchLower) ||
          unit.group_name?.toLowerCase().includes(searchLower) ||
          unit.site_name?.toLowerCase().includes(searchLower);
        if (!matchesHierarchyResult && !matchesDirectSearch) return false;
      }

      // Industry type filter - only apply if user has multiple industries
      if (hasMultipleIndustries && selectedIndustryTypes.length > 0 && !selectedIndustryTypes.includes(unitIndustryType)) {
        return false;
      }

      // Model filter (extract first 3 chars of unit_code)
      const modelPrefix = unit.unit_code?.substring(0, 3) || '';
      if (selectedModels.length > 0 && !selectedModels.includes(modelPrefix)) {
        return false;
      }

      // Status filter (use real-time status from TruckStatusContext)
      const liveStatus = getStatus(unit.id);
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(liveStatus)) {
        return false;
      }

      // Company filter
      if (selectedCompanyIds.length > 0 && unit.company_id && !selectedCompanyIds.includes(unit.company_id)) {
        return false;
      }

      // Group filter (including unassigned handling)
      if (selectedGroupIds.length > 0) {
        const matchesGroup = (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
                            (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);
        if (!matchesGroup) return false;
      }

      return true;
    });

    console.log('[Units Page] Filtered result:', {
      filteredCount: filtered.length,
      originalCount: units.length
    });

    return filtered;
  }, [units, searchTerm, matchesHierarchy, selectedIndustryTypes, selectedModels, selectedStatuses, selectedCompanyIds, selectedGroupIds, getStatus, accessibleIndustries, hasMultipleIndustries]);

  // Group filtered units by industry for display
  const unitsByIndustry = useMemo(() => {
    const grouped: Record<IndustryType, SettingsUnitRow[]> = {} as Record<IndustryType, SettingsUnitRow[]>;

    // Initialize all accessible industries with empty arrays
    accessibleIndustries.forEach(industry => {
      grouped[industry] = [];
    });

    // Group units by their industry_type
    filteredUnits.forEach(unit => {
      const industry = unit.industry_type || 'film';
      if (grouped[industry]) {
        grouped[industry].push(unit);
      }
    });

    return grouped;
  }, [filteredUnits, accessibleIndustries]);

  // Set initial active industry tab when industries are available
  useEffect(() => {
    if (hasMultipleIndustries && accessibleIndustries.length > 0 && !activeIndustryTab) {
      setActiveIndustryTab(accessibleIndustries[0]);
    }
  }, [accessibleIndustries, hasMultipleIndustries, activeIndustryTab]);

  const handleAddUnit = () => {
    setEditingUnit(null);
    // Default to user's first accessible industry (or only industry if single access)
    const defaultIndustryType = accessibleIndustries[0] || 'film';
    setFormData({
      unit_code: '',
      unit_model: '',      // Reset generator fields
      unit_number: '',
      unit_origin: '',
      name: '',
      vin: '',
      status: 'active',
      industry_type: defaultIndustryType,
      company_id: '',
      group_id: '',
      site_id: '',
      latitude: '',
      longitude: '',
    });
    // Reset global check state
    setIsGlobalDuplicate(null);
    setIsCheckingGlobal(false);
    setDrawerOpen(true);
  };

  const handleEdit = (unit: SettingsUnitRow) => {
    setEditingUnit(unit);
    // Reset global check state
    setIsGlobalDuplicate(null);
    setIsCheckingGlobal(false);

    // Determine group_id and site_id from primary_group_id
    // If the unit's primary_group_id is a child group (has parent), that's the site
    // If it's a root group (no parent), that's the group itself with no site
    const directGroup = groups.find((g) => g.id === unit.primary_group_id);
    let groupId = '';
    let siteId = '';

    if (directGroup) {
      if (directGroup.parent_group_id) {
        // This is a child/site group - set parent as group, this as site
        groupId = directGroup.parent_group_id;
        siteId = directGroup.id;
      } else {
        // This is a root/parent group - set as group, no site
        groupId = directGroup.id;
        siteId = '';
      }
    }

    // Parse unit code into components for the generator fields
    const parsedCode = parseUnitCode(unit.unit_code || '');

    setFormData({
      unit_code: unit.unit_code || '',
      unit_model: parsedCode?.model || '',
      unit_number: parsedCode?.number || '',
      unit_origin: parsedCode?.origin || '',
      name: unit.name || '',
      vin: unit.vin || '',
      status: unit.status || 'active',
      industry_type: unit.industry_type || '',
      company_id: unit.company_id || '',
      group_id: groupId,
      site_id: siteId,
      latitude: unit.latitude?.toString() || '',
      longitude: unit.longitude?.toString() || '',
    });
    setDrawerOpen(true);
  };

  const handleDelete = async (unit: SettingsUnitRow) => {
    if (!confirm(`Delete unit "${unit.unit_id}"? This action cannot be undone.`)) return;

    try {
      await deleteAdminUnit(unit.id);
      trackAdminAction('unit', 'deleted', unit.id);
      toast.success('Unit deleted');
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to delete unit');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Convert group_id/site_id back to primary_group_id
      // If site is selected, use site_id; otherwise use group_id
      const primary_group_id = formData.site_id || formData.group_id || undefined;

      const payload: Record<string, unknown> = {
        unit_code: formData.unit_code,
        name: formData.name,
        vin: formData.vin,
        status: formData.status,
        model_code: formData.unit_model, // Use unit_model from generator
        industry_type: formData.industry_type,
        company_id: formData.company_id,
        primary_group_id,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
      };

      if (editingUnit) {
        // Update existing unit
        await updateAdminUnit(editingUnit.id, payload);
        trackAdminAction('unit', 'updated', editingUnit.id);
        toast.success('Unit updated');
      } else {
        // Create new unit
        const response = await createAdminUnit(payload);
        trackAdminAction('unit', 'created');
        // Say what came along (T-058 #4): whether the Sol-Ark / Starlink named
        // after this unit linked, and what happens next if not.
        toast.success(describeUnitCreated(response?.unit?.unit_code ?? formData.unit_code, response?.auto_link), { autoClose: 10000 });

        // If this was created from a new device, mark the device as created
        if (selectedNewDevice && response?.unit?.id) {
          await markDeviceCreated(selectedNewDevice.id, response.unit.id);
          setSelectedNewDevice(null);
        }
      }
      setDrawerOpen(false);
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || (editingUnit ? 'Failed to update unit' : 'Failed to create unit'));
    }
  };

  /**
   * Unlink Starlink terminal from unit (superadmin only)
   */
  const handleUnlinkStarlink = async () => {
    if (!editingUnit || !isSuperadmin) return;

    const confirmed = window.confirm(
      `Are you sure you want to unlink the Starlink terminal from ${editingUnit.unit_code}?\n\nThe terminal record will be preserved and available for reassignment.`
    );
    if (!confirmed) return;

    setIsUnlinkingStarlink(true);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${editingUnit.id}/unlink-starlink`, {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unlink Starlink');
      }
      toast.success('Starlink terminal unlinked successfully');
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to unlink Starlink terminal');
    } finally {
      setIsUnlinkingStarlink(false);
    }
  };

  /**
   * Unlink Sol-Ark inverter from unit (superadmin only)
   */
  const handleUnlinkSolark = async () => {
    if (!editingUnit || !isSuperadmin) return;

    const confirmed = window.confirm(
      `Are you sure you want to unlink the Sol-Ark inverter from ${editingUnit.unit_code}?\n\nThe inverter record will be preserved and available for reassignment.`
    );
    if (!confirmed) return;

    setIsUnlinkingSolark(true);
    try {
      // Name the device: a unit may hold two inverters, and the backend
      // returns an ambiguous_device 409 rather than guess between them.
      const solarDeviceId = editingUnit.systems?.solar?.deviceId;
      const response = await authenticatedFetch(`/api/v1/admin/units/${editingUnit.id}/unlink-solark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(solarDeviceId ? { body: JSON.stringify({ deviceId: solarDeviceId }) } : {}),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unlink Sol-Ark');
      }
      toast.success('Sol-Ark inverter unlinked successfully');
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to unlink Sol-Ark inverter');
    } finally {
      setIsUnlinkingSolark(false);
    }
  };

  /**
   * Unlink Omni GPS from unit (superadmin only)
   */
  const handleUnlinkOmni = async () => {
    if (!editingUnit || !isSuperadmin) return;

    const confirmed = window.confirm(
      `Unlink Omni GPS from ${editingUnit.unit_code}?\n\nThe device record will be preserved and available for reassignment.`
    );
    if (!confirmed) return;

    setIsUnlinkingOmni(true);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${editingUnit.id}/unlink-omni`, {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error((error as { message?: string }).message || 'Failed to unlink Omni GPS');
        return;
      }
      toast.success('Omni GPS unlinked');
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to unlink Omni GPS';
      toast.error(message);
    } finally {
      setIsUnlinkingOmni(false);
    }
  };

  const handleOpenBulkApproval = async () => {
    setBulkApprovalOpen(true);
    setBulkApprovalPreview(null);
    setBulkApprovalGroupId('');
    setBulkApprovalSiteId('');

    setIsLoadingBulkPreview(true);
    try {
      const preview = await getBulkApprovalPreview();
      setBulkApprovalPreview(preview);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to preview pending units';
      toast.error(message);
    } finally {
      setIsLoadingBulkPreview(false);
    }
  };

  const handleApprovePendingUnits = async () => {
    if (!bulkApprovalPreview) return;

    const readyRows = bulkApprovalPreview.rows.filter((row) => row.status !== 'conflict');
    const rowsToCreate = readyRows.filter((row) => row.status === 'ready_create');
    const requiresAssignment = rowsToCreate.length > 0;
    const selectedPrimaryGroupId = bulkApprovalSiteId || bulkApprovalGroupId;
    const assignment = groups.find((group) => group.id === selectedPrimaryGroupId);

    if (readyRows.length === 0) {
      toast.warning('No unambiguous pending units are ready to approve');
      return;
    }
    if (requiresAssignment && !assignment?.company_id) {
      toast.error(`Select a ${getLabel('group').toLowerCase()} before approving units`);
      return;
    }

    const companyId = requiresAssignment
      ? assignment?.company_id
      : readyRows.find((row) => row.targetCompanyId)?.targetCompanyId;
    if (!companyId) {
      toast.error('The existing Fleet unit company could not be resolved');
      return;
    }

    const primaryGroupId = requiresAssignment ? selectedPrimaryGroupId : null;
    const company = companies.find((item) => item.id === companyId);
    try {
      const result = await approvePending({
        deviceIds: readyRows.map((row) => row.id),
        companyId,
        primaryGroupId,
        industryType: company?.industry || accessibleIndustries[0] || 'film',
      });

      if (result.approved > 0) {
        toast.success(
          `${result.approved} pending unit${result.approved !== 1 ? 's' : ''} approved` +
          `${result.created ? ` (${result.created} created)` : ''}`,
        );
      }
      if (result.skipped > 0 || result.failed > 0 || bulkApprovalPreview.conflicts > 0) {
        const unresolved = result.skipped + result.failed + bulkApprovalPreview.conflicts;
        toast.warning(
          `${unresolved} device${unresolved !== 1 ? 's remain' : ' remains'} pending for individual review`,
        );
      }

      await reloadAfterFleetMutation();

      if (result.skipped > 0 || result.failed > 0) {
        // Keep the review context open when the server reports row-level
        // failures so the remaining pending rows are not hidden behind a toast.
        setBulkApprovalPreview(await getBulkApprovalPreview());
        return;
      }

      setBulkApprovalOpen(false);
      setBulkApprovalPreview(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to approve pending units';
      toast.error(message);
    }
  };

  const handleBulkEdit = (units: SettingsUnitRow[]) => {
    setSelectedUnits(units);
    setFormData({
      unit_code: '',
      unit_model: '',      // For bulk edit, model is independent (not tied to unit_code)
      unit_number: '',
      unit_origin: '',
      name: '',
      vin: '',
      status: '__no_change__',
      industry_type: '',
      company_id: '',
      group_id: '__no_change__',
      site_id: '__no_change__',
      latitude: '',
      longitude: '',
    });
    setBulkDrawerOpen(true);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const primaryFromSite =
        formData.site_id && formData.site_id !== '' && formData.site_id !== '__no_change__'
          ? formData.site_id
          : undefined;
      const primaryFromGroup =
        formData.group_id && formData.group_id !== '' && formData.group_id !== '__no_change__'
          ? formData.group_id
          : undefined;
      const primaryGroupId = primaryFromSite ?? primaryFromGroup;

      const companyReassign = formData.company_id?.trim();

      // Company changes go through deploy (updates company_id and clears/applies group like deployment flow)
      if (companyReassign) {
        await Promise.all(
          selectedUnits.map(async (unit) => {
            const response = await authenticatedFetch(`/api/v1/admin/units/${unit.id}/deploy`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                company_id: companyReassign,
                group_id: primaryGroupId ?? null,
                deployment_reason: 'Bulk company reassignment',
              }),
            });
            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw new Error(
                (err as { message?: string }).message ||
                  `Failed to reassign company for ${unit.unit_id}`
              );
            }
          })
        );
      }

      const updates: Record<string, unknown> = {};

      if (formData.name?.trim()) updates.name = formData.name.trim();
      if (formData.status && formData.status !== '' && formData.status !== '__no_change__') {
        updates.status = formData.status;
      }
      if (formData.unit_model?.trim()) updates.model_code = formData.unit_model.trim();
      if (formData.industry_type?.trim()) updates.industry_type = formData.industry_type.trim();

      if (!companyReassign && primaryGroupId) {
        updates.primary_group_id = primaryGroupId;
      }

      if (formData.latitude?.trim()) {
        updates.latitude = parseFloat(formData.latitude);
      }
      if (formData.longitude?.trim()) {
        updates.longitude = parseFloat(formData.longitude);
      }

      if (Object.keys(updates).length > 0) {
        await Promise.all(selectedUnits.map((unit) => updateAdminUnit(unit.id, updates)));
      }

      toast.success('Units updated successfully');
      setBulkDrawerOpen(false);
      reloadAfterFleetMutation();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update units';
      toast.error(message);
    }
  };

  // Bulk rate assignment handler
  const handleBulkRateAssignment = async (data: BulkRateAssignmentRequest): Promise<BulkRateAssignmentResponse | null> => {
    setIsSubmittingRate(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result: BulkRateAssignmentResponse = await response.json();

      if (result.created > 0) {
        toast.success(`Rate assigned to ${result.created} unit${result.created !== 1 ? 's' : ''}`);
      }
      if (result.skipped > 0) {
        toast.warning(`${result.skipped} unit${result.skipped !== 1 ? 's' : ''} skipped (overlapping dates)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} error${result.errors.length !== 1 ? 's' : ''} occurred`);
      }

      return result;
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to assign rates');
      return null;
    } finally {
      setIsSubmittingRate(false);
    }
  };

  const columns: Column<SettingsUnitRow>[] = [
    {
      key: 'status',
      label: <Circle className="w-3 h-3" />,
      width: '32px',
      align: 'center',
      render: (unit) => {
        const liveStatus = getStatus(unit.id);
        return (
          <span
            className={`h-2.5 w-2.5 rounded-full inline-block ${
              liveStatus === 'online' ? 'bg-success' :
              liveStatus === 'idle' ? 'bg-muted-foreground' :
              liveStatus === 'unknown' ? 'bg-muted-foreground/50' :
              'bg-error'
            }`}
            title={liveStatus}
          />
        );
      },
    },
    {
      key: 'unit_id',
      label: 'Unit ID',
      sortable: true,
      width: '180px',
      render: (unit) => (
        <div className="flex items-center gap-2">
          <div className="bg-muted p-1.5 rounded text-[#8FB560] flex-shrink-0">
            <Truck className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="font-medium text-foreground whitespace-nowrap block">{unit.unit_id}</span>
            {(unit.city || unit.state_province) && (
              <span className="text-xs text-muted-foreground truncate block">
                {[unit.city, unit.state_province].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'systems',
      label: 'Systems',
      width: '100px',
      render: (unit) => <SystemStatusIcons unit={settingsUnitRowToFleetUnit(unit)} variant="compact" />,
    },
    {
      key: 'solark_name',
      label: 'Sol-Ark',
      width: '150px',
      render: (unit) => <SolArkPlantCell solar={unit.systems?.solar} />,
    },
    {
      key: 'unit_status',
      label: 'Status',
      width: '130px',
      render: (unit) => {
        const isUpdatingStatus = updatingUnitIds.has(unit.id);
        return (
        <Select
          value={unit.status}
          disabled={isUpdatingStatus}
          onValueChange={async (val) => {
            setUpdatingUnitIds((prev) => new Set(prev).add(unit.id));
            try {
              await updateAdminUnit(unit.id, { status: val });
              toast.success(`${unit.unit_code} set to ${val}`);
              reloadAfterFleetMutation();
            } catch {
              toast.error('Failed to update status');
            } finally {
              setUpdatingUnitIds((prev) => {
                const next = new Set(prev);
                next.delete(unit.id);
                return next;
              });
            }
          }}
        >
          <SelectTrigger className="h-6 text-xs border-none bg-transparent px-1 gap-1 w-auto">
            {isUpdatingStatus ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />
                <span className="sr-only">Updating status…</span>
              </span>
            ) : (
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0',
              unit.status === 'active' ? 'border-blue-500/40 text-blue-500' :
              unit.status === 'maintenance' ? 'border-purple-500/40 text-purple-500' :
              unit.status === 'off_rent' ? 'border-amber-500/40 text-amber-500' :
              'border-muted-foreground/40 text-muted-foreground'
            )}>
              {unit.status === 'off_rent' ? 'Off Rent' : unit.status?.charAt(0).toUpperCase() + unit.status?.slice(1)}
            </Badge>
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="off_rent">Off Rent</SelectItem>
          </SelectContent>
        </Select>
        );
      },
    },
    {
      key: 'site',
      label: 'Site',
      width: '200px',
      render: (unit) => (
        <div className="flex items-center gap-1.5 text-xs">
          <Building2 className="w-3.5 h-3.5 text-[#8FB560] flex-shrink-0" />
          <span className="text-foreground truncate">{unit.site_name || '—'}</span>
        </div>
      ),
    },
    {
      key: 'group_name',
      label: getLabel('group'),
      width: '220px',
      render: (unit) => (
        <span className="text-sm text-foreground truncate block">{unit.group_name}</span>
      ),
    },
    {
      key: 'company_name',
      label: 'Company',
      width: '180px',
      render: (unit) => (
        <span className="text-sm text-foreground truncate block">{unit.company_name}</span>
      ),
    },
    {
      key: 'first_seen_at',
      label: 'First Added',
      width: '100px',
      render: (unit) => {
        if (!unit.first_seen_at) return <span className="text-muted-foreground">—</span>;
        const date = new Date(unit.first_seen_at);
        return (
          <span className="text-sm text-muted-foreground whitespace-nowrap" title={date.toLocaleString()}>
            {format(date, 'MMM d, yyyy')}
          </span>
        );
      },
    },
    {
      key: 'last_comms_at',
      label: 'Last Comms',
      width: '120px',
      render: (unit) => {
        // No communication data yet
        if (!unit.last_comms_at) {
          return <span className="text-muted-foreground italic">Awaiting sync</span>;
        }
        const date = new Date(unit.last_comms_at);
        const now = new Date();
        const diffMinutes = (now.getTime() - date.getTime()) / (1000 * 60);
        const diffHours = diffMinutes / 60;

        // Determine status
        let statusIcon = null;
        let statusClass = 'text-muted-foreground';
        if (diffMinutes <= 15) {
          // Recent (green checkmark)
          statusIcon = <span className="text-success ml-1">✓</span>;
          statusClass = 'text-foreground';
        } else if (diffHours > 1) {
          // Stale (amber warning)
          statusIcon = <AlertCircle className="w-3 h-3 text-amber-500 ml-1" />;
          statusClass = 'text-amber-500';
        }

        return (
          <span className={`text-sm flex items-center whitespace-nowrap ${statusClass}`} title={date.toLocaleString()}>
            {formatDistanceToNow(date, { addSuffix: true })}
            {statusIcon}
          </span>
        );
      },
    },
  ];

  const rowActions: Action<SettingsUnitRow>[] = [
    {
      label: 'Edit',
      icon: Edit2,
      onClick: handleEdit,
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: handleDelete,
      variant: 'destructive',
    },
  ];

  const bulkActions: BulkAction<SettingsUnitRow>[] = [
    {
      label: `Assign to ${getLabel('group')}`,
      icon: ClipboardList,
      // AdminDataTable clears its selection right after this fires, so the ids
      // have to be copied out synchronously.
      onClick: (selectedRows) => {
        setBulkAssignPrefill(selectedRows.map((row) => row.id));
        setBulkAssignOpen(true);
      },
    },
    {
      label: 'Bulk Edit Metadata',
      icon: Settings,
      onClick: handleBulkEdit,
    },
    {
      label: 'Set Maintenance',
      icon: Wrench,
      onClick: async (selectedUnits) => {
        if (confirm('Set selected units to Maintenance mode?')) {
          try {
            await Promise.all(
              selectedUnits.map(unit => updateAdminUnit(unit.id, { status: 'maintenance' }))
            );
            toast.success('Units set to maintenance mode');
            reloadAfterFleetMutation();
          } catch (_error) {
            toast.error('Failed to update units');
          }
        }
      }
    },
    {
      label: 'Set Active',
      icon: Truck,
      onClick: async (selectedUnits) => {
        try {
          await Promise.all(
            selectedUnits.map(unit => updateAdminUnit(unit.id, { status: 'active' }))
          );
          toast.success('Units set to active');
          reloadAfterFleetMutation();
        } catch (_error) {
          toast.error('Failed to update units');
        }
      }
    },
    {
      label: 'Reassign Company',
      icon: Building2,
      onClick: (selectedRows) => {
        setSelectedUnits(selectedRows);
        setFormData({
          unit_code: '', unit_model: '', unit_number: '', unit_origin: '',
          name: '', vin: '', status: '__no_change__', industry_type: '',
          company_id: '', group_id: '__no_change__', site_id: '__no_change__',
          latitude: '', longitude: '',
        });
        setBulkDrawerOpen(true);
      },
    },
    {
      label: 'Assign Rate',
      icon: DollarSign,
      onClick: (selectedUnits) => {
        setUnitsForRateAssignment(selectedUnits);
        setBulkRateModalOpen(true);
      },
      disabled: isLoadingRates || rates.length === 0,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">{getLabel('unit')} Management</h1>
              <AuditLogLink resourceTypes={['unit']} tooltip="View unit audit history" />
            </div>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Overview and management of all trailer units</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <SettingsFilterBar
              searchQuery={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search by Unit ID, VIN, Name, Company, or Site..."
            />
            <Button
              variant="outline"
              onClick={() => {
                setBulkAssignPrefill([]);
                setBulkAssignOpen(true);
              }}
              className="whitespace-nowrap w-full sm:w-auto"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Bulk Assign
            </Button>
            <Button onClick={handleAddUnit} className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add {getLabel('unit')}
            </Button>
          </div>
        </div>
      </div>

      {/* Group tables by industry when user has multiple industries, or show tabs when new devices exist */}
      {(hasMultipleIndustries || newDeviceCounts.total > 0) ? (
        <Tabs value={activeIndustryTab} onValueChange={setActiveIndustryTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="bg-transparent border-b border-border rounded-none p-0 h-auto w-full justify-evenly overflow-x-auto">
            {accessibleIndustries.map(industry => {
              const industryUnits = unitsByIndustry[industry] || [];
              return (
                <TabsTrigger
                  key={industry}
                  value={industry}
                  className="flex-1 bg-transparent border-0 rounded-none py-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#8FB560] data-[state=active]:text-foreground gap-3 -mb-px"
                >
                  <span className="font-semibold text-base">{INDUSTRY_TYPE_LABELS[industry]}</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {industryUnits.length}
                  </span>
                </TabsTrigger>
              );
            })}
            {/* New Devices Tab */}
            <TabsTrigger
              value="new-devices"
              className="flex-1 bg-transparent border-0 rounded-none py-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#8FB560] data-[state=active]:text-foreground gap-3 -mb-px"
            >
              <span className="font-semibold text-base">New Devices</span>
              {newDeviceCounts.total > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                  {newDeviceCounts.total}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 flex-1 flex flex-col min-h-0">
            {accessibleIndustries.map(industry => {
              const industryUnits = unitsByIndustry[industry] || [];
              return (
                <TabsContent
                  key={industry}
                  value={industry}
                  className="flex-1 flex-col min-h-0 data-[state=active]:flex mt-0"
                >
                  <AdminDataTable
                    data={industryUnits}
                    columns={columns}
                    loading={loading}
                    emptyMessage={`No ${INDUSTRY_TYPE_LABELS[industry].toLowerCase()} units found`}
                    bulkActions={bulkActions}
                    actions={rowActions}
                    tableId={`units-${industry}`}
                    onRowClick={handleEdit}
                  />
                </TabsContent>
              );
            })}
            {/* New Devices Tab Content */}
            <TabsContent
              value="new-devices"
              className="flex-1 flex-col min-h-0 data-[state=active]:flex mt-0"
            >
              <NewDevicesTable
                devices={newDevices}
                loading={isLoadingNewDevices}
                pendingCount={newDeviceTotalCount}
                approvingAll={isLoadingBulkPreview || isBulkApproving}
                onApproveAll={isSuperadmin ? handleOpenBulkApproval : undefined}
                onCreateUnit={(device) => {
                  // Pre-fill form and open drawer
                  const parsed = parseUnitCode(device.external_name || '');
                  setFormData({
                    ...formData,
                    unit_code: device.external_name || '',
                    unit_model: parsed?.model || '',
                    unit_number: parsed?.number || '',
                    unit_origin: parsed?.origin || '',
                  });
                  setSelectedNewDevice(device);
                  setEditingUnit(null);
                  setDrawerOpen(true);
                }}
                onLinkToUnit={async (device) => {
                  setSelectedNewDevice(device);
                  setLinkSearchTerm('');
                  setLinkSearchResults([]);
                  setSelectedUnitForLink(null);
                  setIsLoadingSuggestions(true);
                  setLinkDrawerOpen(true);
                  const suggestions = await getSuggestions(device.id);
                  setUnitSuggestions(suggestions);
                  setIsLoadingSuggestions(false);
                }}
                onDismiss={async (device) => {
                  if (confirm(`Dismiss "${device.external_name}"? This device won't appear in the new devices list anymore.`)) {
                    await dismissDevice(device.id);
                  }
                }}
              />
            </TabsContent>
          </div>
        </Tabs>
      ) : (
        // Single industry with no new devices: show flat table without grouping
        <AdminDataTable
          data={filteredUnits}
          columns={columns}
          loading={loading}
          emptyMessage="No units found"
          bulkActions={bulkActions}
          actions={rowActions}
          tableId="units"
          onRowClick={handleEdit}
        />
      )}

      {/* Single Unit Edit/Create Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setIsGlobalDuplicate(null);
          setIsCheckingGlobal(false);
        }}
        title={editingUnit ? `Edit Unit: ${editingUnit.unit_id}` : 'Add New Unit'}
        description={editingUnit ? 'Update unit metadata and configuration' : 'Create a new unit with the fields below'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Section: Identification */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Identification</h3>

            {/* Unit Code Generator - Bidirectional Input System */}
            <div className="space-y-3">
              <Label>Unit Code</Label>

              {/* Main unit code input with duplicate validation */}
              <div className="relative">
                <Input
                  id="unit_code"
                  value={formData.unit_code}
                  onChange={handleUnitCodeChange}
                  placeholder="Enter code or use fields below"
                  className={`!bg-background font-mono uppercase ${
                    isDuplicateUnitCode
                      ? 'border-red-500 focus:border-red-500'
                      : formData.unit_code.trim()
                        ? 'border-[#8FB560] focus:border-[#8FB560]'
                        : 'border-border'
                  }`}
                />
                {isDuplicateUnitCode && (
                  <span className="absolute -top-2 right-2 z-10 px-1.5 py-0.5 text-[10px] font-medium text-red-400 bg-card border border-red-500 rounded">
                    Already exists
                  </span>
                )}
                {isCheckingGlobal && !isDuplicateUnitCode && (
                  <span className="absolute -top-2 right-2 z-10 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-card border border-border rounded animate-pulse">
                    Checking...
                  </span>
                )}
              </div>

              {/* Component inputs in a row: Model + Unit # + Origin */}
              <div className="grid grid-cols-3 gap-2">
                {/* Model Dropdown */}
                <div>
                  <Label className="text-xs text-muted-foreground">Model</Label>
                  <Select value={formData.unit_model} onValueChange={handleModelChange}>
                    <SelectTrigger className="bg-background border-border h-9">
                      <SelectValue placeholder="---">
                        {formData.unit_model ? (
                          <span>{formData.unit_model}</span>
                        ) : (
                          <span className="text-muted-foreground">---</span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {accessibleModels.map(([code, label]) => (
                        <SelectItem key={code} value={code}>
                          {code} - {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Unit Number Counter */}
                <div>
                  <Label className="text-xs text-muted-foreground">Unit #</Label>
                  <div className="flex items-center h-9">
                    <button
                      type="button"
                      onClick={handleNumberDecrement}
                      disabled={!formData.unit_model || !formData.unit_origin}
                      className="h-full px-2 bg-card border border-border border-r-0 rounded-l hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      value={formData.unit_number}
                      onChange={handleNumberChange}
                      placeholder="001"
                      className="bg-background border-border text-center h-full rounded-none border-x-0 w-14 px-1"
                    />
                    <button
                      type="button"
                      onClick={handleNumberIncrement}
                      disabled={!formData.unit_model || !formData.unit_origin}
                      className="h-full px-2 bg-card border border-border border-l-0 rounded-r hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {/* Origin Dropdown */}
                <div>
                  <Label className="text-xs text-muted-foreground">Origin</Label>
                  <Select value={formData.unit_origin} onValueChange={handleOriginChange}>
                    <SelectTrigger className="bg-background border-border h-9">
                      <SelectValue placeholder="---">
                        {formData.unit_origin ? (
                          <span>{formData.unit_origin}</span>
                        ) : (
                          <span className="text-muted-foreground">---</span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {ORIGIN_GROUPS.map((group) => (
                        <SelectGroup key={group.label}>
                          <SelectLabel className="text-muted-foreground text-xs uppercase tracking-wide">{group.label}</SelectLabel>
                          {group.origins.map((origin) => (
                            <SelectItem key={origin.code} value={origin.code}>
                              {origin.code} - {origin.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* VIN field */}
            <div>
              <Label htmlFor="vin">VIN</Label>
              <Input
                id="vin"
                value={formData.vin}
                onChange={e => setFormData({...formData, vin: e.target.value})}
                className="bg-background border-border font-mono"
              />
            </div>

            {/* Name field */}
            <div>
              <Label htmlFor="name">Name (Optional)</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Main Office Trailer"
                className="bg-background border-border"
              />
            </div>
          </div>

          {/* Section: Status - only show when editing (status is auto-set for new units) */}
          {editingUnit && (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Status</h3>
              <div className={hasMultipleIndustries ? "grid grid-cols-2 gap-4" : ""}>
                <div>
                  <Label htmlFor="status">Current Status</Label>
                  <Select value={formData.status} onValueChange={val => setFormData({...formData, status: val})}>
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="off_rent">Off Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Only show Industry Type dropdown if user has access to multiple industries */}
                {hasMultipleIndustries && (
                  <div>
                    <Label htmlFor="industry_type">Industry Type</Label>
                    <Select value={formData.industry_type} onValueChange={val => setFormData({...formData, industry_type: val})}>
                      <SelectTrigger className="bg-background border-border">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {accessibleIndustries.map((type) => (
                          <SelectItem key={type} value={type}>
                            {INDUSTRY_TYPE_LABELS[type]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Industry Type for new units (when user has multiple industries) */}
          {!editingUnit && hasMultipleIndustries && (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Classification</h3>
              <div>
                <Label htmlFor="industry_type">Industry Type</Label>
                <Select value={formData.industry_type} onValueChange={val => setFormData({...formData, industry_type: val})}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessibleIndustries.map((type) => (
                      <SelectItem key={type} value={type}>
                        {INDUSTRY_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Section: Assignment */}
          <div className="space-y-4 border-t border-border pt-4">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Assignment</h3>

            {/* Group dropdown (parent/group level) */}
            <div>
              <Label htmlFor="group_id">{getLabel('group')}</Label>
              <Select
                value={formData.group_id}
                onValueChange={val => {
                  // When group changes, clear the site selection and set company_id from the selected group
                  const selectedGroup = groups.find((g) => g.id === val);
                  setFormData({
                    ...formData,
                    group_id: val,
                    site_id: '',
                    company_id: selectedGroup?.company_id || ''
                  });
                }}
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder={`Select ${getLabel('group').toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {groups
                    .filter((g) => !g.parent_group_id)
                    .map((parentGroup) => (
                      <SelectItem key={parentGroup.id} value={parentGroup.id}>
                        {parentGroup.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Site dropdown (child level) - disabled until group is selected */}
            <div>
              <Label htmlFor="site_id">{getLabel('site')}</Label>
              <Select
                value={formData.site_id}
                onValueChange={val => setFormData({...formData, site_id: val})}
                disabled={!formData.group_id}
              >
                <SelectTrigger className={`bg-background border-border ${!formData.group_id ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <SelectValue placeholder={formData.group_id ? `Select ${getLabel('site').toLowerCase()}` : `Select ${getLabel('group').toLowerCase()} first`} />
                </SelectTrigger>
                <SelectContent>
                  {/* Only show sites that belong to the selected group */}
                  {groups
                    .filter((g) => g.parent_group_id === formData.group_id)
                    .map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location and Systems - only show when editing (auto-set for new units) */}
          {editingUnit && (
            <>
              {/* Section: Location */}
              <div className="space-y-4 border-t border-border pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Location</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={e => setFormData({...formData, latitude: e.target.value})}
                      placeholder="34.0522"
                      className="bg-background border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={e => setFormData({...formData, longitude: e.target.value})}
                      placeholder="-118.2437"
                      className="bg-background border-border"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Linked Systems - Shows actual device status */}
              <div className="space-y-4 border-t border-border pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Linked Systems</h3>
                <div className="space-y-3">
                  {/* Starlink */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Wifi className={cn(
                        'h-4 w-4',
                        getSystemIconColor('starlink', editingUnit.systems?.starlink?.connectionStatus === 'online')
                      )} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Starlink</span>
                        {editingUnit.systems?.starlink?.enabled ? (
                          <span className="text-xs text-muted-foreground font-mono">
                            {editingUnit.systems.starlink.externalId || 'Terminal linked'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not linked</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingUnit.systems?.starlink?.enabled ? (
                        <Badge variant={connectionStatusVariant(editingUnit.systems.starlink.connectionStatus)}>
                          {editingUnit.systems.starlink.connectionStatus}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                      {/* Unlink button - superadmin only */}
                      {isSuperadmin && editingUnit.systems?.starlink?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleUnlinkStarlink}
                          disabled={isUnlinkingStarlink}
                          className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Unlink Starlink terminal"
                        >
                          {isUnlinkingStarlink ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2Off className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {/* Link button - superadmin only, when not linked */}
                      {isSuperadmin && !editingUnit.systems?.starlink?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openDevicePicker('starlink')}
                          className="h-8 px-2 text-[#8FB560] hover:text-[#8FB560] hover:bg-[#8FB560]/10"
                          title="Link Starlink terminal"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Sol-Ark Solar */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Sun className={cn(
                        'h-4 w-4',
                        getSystemIconColor('solar', (editingUnit.systems?.solar?.generation ?? 0) > 0)
                      )} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Sol-Ark Solar</span>
                        {editingUnit.systems?.solar?.enabled ? (
                          <>
                            <span className="text-xs text-foreground">
                              {editingUnit.systems.solar.name || 'Unnamed Sol-Ark plant'}
                            </span>
                            {editingUnit.systems.solar.externalId && (
                              <span className="text-[10px] text-muted-foreground font-mono">
                                Plant ID: {editingUnit.systems.solar.externalId}
                                {/* Multi-inverter plants (WEA 53ft) show their physical
                                    count; stats stay COMBINED plant-level by design. */}
                                {(editingUnit.systems.solar.physicalInverterCount ?? 0) > 1 &&
                                  ` · ${editingUnit.systems.solar.physicalInverterCount} inverters`}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not linked</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingUnit.systems?.solar?.enabled ? (
                        <Badge variant={(editingUnit.systems.solar.generation ?? 0) > 0 ? 'default' : 'secondary'}>
                          {(editingUnit.systems.solar.generation ?? 0) > 0 ? 'Generating' : 'Idle'}
                          {editingUnit.systems.solar.battery > 0 && ` • ${editingUnit.systems.solar.battery}%`}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                      {/* Unlink button - superadmin only */}
                      {isSuperadmin && editingUnit.systems?.solar?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleUnlinkSolark}
                          disabled={isUnlinkingSolark}
                          className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Unlink Sol-Ark inverter"
                        >
                          {isUnlinkingSolark ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2Off className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {/* Link button - superadmin only, when not linked */}
                      {isSuperadmin && !editingUnit.systems?.solar?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openDevicePicker('solark')}
                          className="h-8 px-2 text-[#8FB560] hover:text-[#8FB560] hover:bg-[#8FB560]/10"
                          title="Link Sol-Ark inverter"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Tesla Charger */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Zap className={cn(
                        'h-4 w-4',
                        getSystemIconColor('charger', !!editingUnit.systems?.charger?.enabled)
                      )} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Tesla Charger</span>
                        {editingUnit.systems?.charger?.enabled ? (
                          <span className="text-xs text-muted-foreground font-mono">
                            {editingUnit.systems.charger.externalId || 'Charger linked'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not linked</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingUnit.systems?.charger?.enabled ? (
                        <Badge variant={editingUnit.systems.charger.status === 'charging' ? 'default' : 'secondary'}>
                          {editingUnit.systems.charger.status}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                      {isSuperadmin && !editingUnit.systems?.charger?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openDevicePicker('tesla')}
                          className="h-8 px-2 text-[#8FB560] hover:text-[#8FB560] hover:bg-[#8FB560]/10"
                          title="Link Tesla charger"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* HVAC System */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Wind className={cn(
                        'h-4 w-4',
                        getSystemIconColor('hvac', !!(editingUnit.systems?.hvac?.enabled && editingUnit.systems.hvac.mode !== 'off'))
                      )} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">HVAC System</span>
                        {editingUnit.systems?.hvac?.enabled ? (
                          <span className="text-xs text-muted-foreground font-mono">
                            {editingUnit.systems.hvac.externalId || 'HVAC linked'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not linked</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingUnit.systems?.hvac?.enabled ? (
                        <Badge variant={editingUnit.systems.hvac.mode !== 'off' ? 'default' : 'secondary'}>
                          {editingUnit.systems.hvac.mode}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                      {isSuperadmin && !editingUnit.systems?.hvac?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openDevicePicker('hvac')}
                          className="h-8 px-2 text-[#8FB560] hover:text-[#8FB560] hover:bg-[#8FB560]/10"
                          title="Link HVAC device"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Omni GPS */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <MapPin className={cn(
                        'h-4 w-4',
                        editingUnit.systems?.omni?.enabled ? 'text-purple-400' : 'text-muted-foreground/30'
                      )} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">GPS Source</span>
                        {editingUnit.systems?.omni?.enabled ? (
                          <span className="text-xs text-purple-400 font-mono">
                            {editingUnit.systems.omni.externalId || 'Omni linked'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not linked</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingUnit.systems?.omni?.enabled ? (
                        <Badge variant="default" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                          GPS
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                      {isSuperadmin && editingUnit.systems?.omni?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleUnlinkOmni}
                          disabled={isUnlinkingOmni}
                          className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Unlink Omni GPS"
                        >
                          {isUnlinkingOmni ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2Off className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {isSuperadmin && !editingUnit.systems?.omni?.enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openDevicePicker('omni')}
                          className="h-8 px-2 text-purple-400 hover:text-purple-400 hover:bg-purple-400/10"
                          title="Link Omni GPS"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: QR Code */}
              <div className="space-y-3 border-t border-border pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">QR Code</h3>
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <QrCode className="h-4 w-4 text-[#8FB560]" />
                    <span className="text-sm">Check-In QR</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" asChild>
                    <Link to="/settings/qr-codes">Manage QR Codes</Link>
                  </Button>
                </div>
              </div>

              {/* Section: Rate Assignments */}
              <div className="border-t border-border pt-4">
                <EntityRateAssignmentSection
                  entityType="unit"
                  entityId={editingUnit.id}
                  entityName={editingUnit.unit_code || editingUnit.name || 'Unit'}
                />
              </div>

              {/* Section: Activity History */}
              <div className="border-t border-border pt-4">
                <EntityAuditTimeline
                  entityType="unit"
                  entityId={editingUnit.id}
                  entityName={editingUnit.unit_code || editingUnit.name || 'Unit'}
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isDuplicateUnitCode}
            >
              {editingUnit ? 'Save Changes' : 'Create Unit'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Edit Drawer */}
      <AdminFormDrawer
        open={bulkDrawerOpen}
        onClose={() => setBulkDrawerOpen(false)}
        title={`Bulk Edit: ${selectedUnits.length} Units`}
        description="Update multiple units at once. Empty fields will not be changed."
      >
        <form onSubmit={handleBulkSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-600 dark:text-blue-400">
            Editing {selectedUnits.length} units. Only filled fields will be updated.
          </div>

          <div>
            <Label htmlFor="bulk_name">Name (Optional)</Label>
            <Input
              id="bulk_name"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="Leave empty for no change"
              className="bg-background border-border"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bulk_status">Status</Label>
              <Select value={formData.status} onValueChange={val => setFormData({...formData, status: val})}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="No change" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__no_change__">No change</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="off_rent">Off Rent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bulk_industry_type">Industry Type</Label>
              <Input
                id="bulk_industry_type"
                value={formData.industry_type}
                onChange={e => setFormData({...formData, industry_type: e.target.value})}
                placeholder="Leave empty for no change"
                className="bg-background border-border"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bulk_model">Model</Label>
            <Select value={formData.unit_model || '__no_change__'} onValueChange={val => setFormData({...formData, unit_model: val === '__no_change__' ? '' : val})}>
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                {accessibleModels.map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {code} - {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bulk_company">Company</Label>
            <Select value={formData.company_id || '__no_change__'} onValueChange={val => setFormData({...formData, company_id: val === '__no_change__' ? '' : val})}>
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cascading Group and Site dropdowns for bulk edit */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bulk_group">{getLabel('group')}</Label>
              <Select
                value={formData.group_id}
                onValueChange={val => {
                  // When group changes, reset site to no change
                  setFormData({...formData, group_id: val, site_id: '__no_change__'});
                }}
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="No change" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__no_change__">No change</SelectItem>
                  {groups
                    .filter((g) => !g.parent_group_id)
                    .map((parentGroup) => (
                      <SelectItem key={parentGroup.id} value={parentGroup.id}>
                        {parentGroup.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="bulk_site">{getLabel('site')}</Label>
              <Select
                value={formData.site_id}
                onValueChange={val => setFormData({...formData, site_id: val})}
                disabled={!formData.group_id || formData.group_id === '__no_change__'}
              >
                <SelectTrigger className={`bg-background border-border ${!formData.group_id || formData.group_id === '__no_change__' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <SelectValue placeholder={formData.group_id && formData.group_id !== '__no_change__' ? 'No change' : `Select ${getLabel('group').toLowerCase()} first`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__no_change__">No change</SelectItem>
                  {groups
                    .filter((g) => g.parent_group_id === formData.group_id)
                    .map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bulk_latitude">Latitude</Label>
              <Input
                id="bulk_latitude"
                type="number"
                step="any"
                value={formData.latitude}
                onChange={e => setFormData({...formData, latitude: e.target.value})}
                placeholder="Leave empty for no change"
                className="bg-background border-border"
              />
            </div>
            <div>
              <Label htmlFor="bulk_longitude">Longitude</Label>
              <Input
                id="bulk_longitude"
                type="number"
                step="any"
                value={formData.longitude}
                onChange={e => setFormData({...formData, longitude: e.target.value})}
                placeholder="Leave empty for no change"
                className="bg-background border-border"
              />
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-xs text-amber-600 dark:text-yellow-400">
            This will update {selectedUnits.length} units. Empty or unchanged fields will not be modified.
          </div>

          <div className="flex gap-3 pt-4 sticky bottom-0 bg-card border-t border-border mt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              Update {selectedUnits.length} Units
            </Button>
            <Button type="button" variant="outline" onClick={() => setBulkDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Assign Units — paste a manifest, stage onto a production/site */}
      <BulkAssignDrawer
        open={bulkAssignOpen}
        onClose={() => {
          setBulkAssignOpen(false);
          setBulkAssignPrefill([]);
        }}
        prefillUnitIds={bulkAssignPrefill}
        groupLabel={getLabel('group')}
        statusLabel={(status) =>
          status === 'off_rent'
            ? 'Off rent'
            : status === 'maintenance'
              ? 'Maintenance'
              : status === 'inactive'
                ? 'Inactive'
                : 'Active'
        }
        onAssigned={() => reloadAfterFleetMutation()}
      />

      {/* Bulk Rate Assignment Modal */}
      <BulkRateAssignmentModal
        open={bulkRateModalOpen}
        onClose={() => {
          setBulkRateModalOpen(false);
          setUnitsForRateAssignment([]);
        }}
        onSubmit={handleBulkRateAssignment}
        entityType="unit"
        selectedEntities={unitsForRateAssignment.map(u => ({
          id: u.id,
          name: u.unit_id || u.unit_code,
          currentRate: undefined,
        }))}
        rates={rates}
        isSubmitting={isSubmittingRate}
      />

      {/* Bulk approve pending devices into Fleet units */}
      <AdminFormDrawer
        open={bulkApprovalOpen}
        onClose={() => {
          if (isBulkApproving) return;
          setBulkApprovalOpen(false);
          setBulkApprovalPreview(null);
          setBulkApprovalGroupId('');
          setBulkApprovalSiteId('');
        }}
        title="Approve Pending Units"
        description="Review every pending source device, then create or link all unambiguous Fleet units at once."
      >
        <div className="space-y-6">
          {isLoadingBulkPreview ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Checking all pending devices…
            </div>
          ) : bulkApprovalPreview ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-xl font-semibold">{bulkApprovalPreview.total}</div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
                <div className="rounded-lg border border-[#8FB560]/30 bg-[#8FB560]/10 p-3">
                  <div className="text-xl font-semibold text-[#8FB560]">{bulkApprovalPreview.ready}</div>
                  <div className="text-xs text-muted-foreground">Ready</div>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="text-xl font-semibold text-amber-400">{bulkApprovalPreview.conflicts}</div>
                  <div className="text-xs text-muted-foreground">Needs review</div>
                </div>
              </div>

              {bulkApprovalPreview.conflicts > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                  <p className="text-xs text-amber-100">
                    Devices with duplicate or invalid source names will stay pending. Plant IDs are shown so they can be reviewed without guessing which physical device is correct.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Approval preview</Label>
                <div className="max-h-[310px] space-y-2 overflow-y-auto pr-1">
                  {bulkApprovalPreview.rows.map((row) => (
                    <div
                      key={row.id}
                      className={cn(
                        'rounded-lg border p-3',
                        row.status === 'conflict'
                          ? 'border-amber-500/30 bg-amber-500/5'
                          : 'border-border bg-muted/20',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">
                            {row.externalName || row.externalId}
                          </div>
                          <div className="truncate font-mono text-[10px] text-muted-foreground">
                            {row.source === 'solark' ? 'Plant' : 'External'} ID: {row.externalId}
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            'shrink-0 text-[10px]',
                            row.status === 'conflict'
                              ? 'border-amber-500/40 text-amber-400'
                              : 'border-[#8FB560]/40 text-[#8FB560]',
                          )}
                        >
                          {row.status === 'conflict'
                            ? 'Review'
                            : row.status === 'ready_link'
                              ? 'Link existing'
                              : 'Create unit'}
                        </Badge>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Fleet unit</span>
                        <span className="font-mono text-foreground">
                          {row.proposedUnitCode || 'No valid code'}
                        </span>
                      </div>
                      {row.reason && (
                        <p className="mt-1.5 text-[11px] leading-relaxed text-amber-300">
                          {row.reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {bulkApprovalPreview.rows.some((row) => row.status === 'ready_create') && (
                <div className="space-y-4 border-t border-border pt-4">
                  <div>
                    <Label>{getLabel('group')}</Label>
                    <Select
                      value={bulkApprovalGroupId}
                      onValueChange={(value) => {
                        setBulkApprovalGroupId(value);
                        setBulkApprovalSiteId('');
                      }}
                    >
                      <SelectTrigger className="mt-2 bg-background border-border">
                        <SelectValue placeholder={`Select ${getLabel('group').toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {groups
                          .filter((group) => !group.parent_group_id)
                          .map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {bulkApprovalGroupId && (
                    <div>
                      <Label>{getLabel('site')} (Optional)</Label>
                      <Select
                        value={bulkApprovalSiteId || '__group_only__'}
                        onValueChange={(value) => setBulkApprovalSiteId(value === '__group_only__' ? '' : value)}
                      >
                        <SelectTrigger className="mt-2 bg-background border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__group_only__">No site — assign to group</SelectItem>
                          {groups
                            .filter((group) => group.parent_group_id === bulkApprovalGroupId)
                            .map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                {group.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setBulkApprovalOpen(false);
                    setBulkApprovalPreview(null);
                  }}
                  disabled={isBulkApproving}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleApprovePendingUnits}
                  disabled={
                    isBulkApproving ||
                    bulkApprovalPreview.ready === 0 ||
                    (bulkApprovalPreview.rows.some((row) => row.status === 'ready_create') && !bulkApprovalGroupId)
                  }
                  className="flex-1 bg-[#8FB560] text-black hover:bg-[#8FB560]/90"
                >
                  {isBulkApproving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Approve {bulkApprovalPreview.ready} Unit{bulkApprovalPreview.ready !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Could not load the pending-unit preview.
            </div>
          )}
        </div>
      </AdminFormDrawer>

      {/* Link Device to Unit Drawer */}
      <AdminFormDrawer
        open={linkDrawerOpen}
        onClose={() => {
          setLinkDrawerOpen(false);
          setSelectedNewDevice(null);
          setUnitSuggestions([]);
          setLinkSearchTerm('');
          setLinkSearchResults([]);
          setSelectedUnitForLink(null);
          setLinkGroupId('');
          setLinkSiteId('');
        }}
        title="Link Device to Unit"
        description="Link this device to an existing unit in the system"
      >
        {selectedNewDevice && (
          <div className="space-y-6 p-6">
            {/* Device Info */}
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {selectedNewDevice.external_name || selectedNewDevice.external_id}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    External ID: {selectedNewDevice.external_id}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                  selectedNewDevice.source === 'solark'
                    ? 'bg-amber-500/10 text-amber-400'
                    : 'bg-sky-500/10 text-sky-400'
                }`}>
                  {selectedNewDevice.source === 'solark' ? <Sun className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                  {selectedNewDevice.source === 'solark' ? 'Sol-Ark' : 'Starlink'}
                </span>
              </div>
            </div>

            {/* Search Input */}
            <div className="space-y-2">
              <Label>Search Units</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by unit code..."
                  value={linkSearchTerm}
                  onChange={async (e) => {
                    const term = e.target.value;
                    setLinkSearchTerm(term);
                    if (term.length >= 2) {
                      setIsSearchingUnits(true);
                      try {
                        const response = await authenticatedFetch(`/api/v1/admin/units/search?q=${encodeURIComponent(term)}&limit=10`);
                        if (response.ok) {
                          const data = await response.json();
                          setLinkSearchResults(data.units || []);
                        }
                      } catch (err) {
                        console.error('Search failed:', err);
                      } finally {
                        setIsSearchingUnits(false);
                      }
                    } else {
                      setLinkSearchResults([]);
                    }
                  }}
                  className="pl-10"
                />
                {isSearchingUnits && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
              </div>
            </div>

            {/* Unit Selection List */}
            <div className="space-y-2">
              <Label>{linkSearchTerm.length >= 2 ? 'Search Results' : 'Suggestions'}</Label>
              <div className="border border-border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                {isLoadingSuggestions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading suggestions...</span>
                  </div>
                ) : (
                  <>
                    {/* Show suggestions when not searching, search results when searching */}
                    {(linkSearchTerm.length >= 2 ? linkSearchResults : unitSuggestions).length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        {linkSearchTerm.length >= 2
                          ? 'No units found matching your search'
                          : 'No suggestions available. Try searching for a unit.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {(linkSearchTerm.length >= 2 ? linkSearchResults : unitSuggestions).map((unit) => {
                          const isSelected = selectedUnitForLink?.id === unit.id;
                          const suggestion = unitSuggestions.find(s => s.id === unit.id);
                          return (
                            <button
                              key={unit.id}
                              type="button"
                              onClick={() => setSelectedUnitForLink(isSelected ? null : { id: unit.id, unit_code: unit.unit_code })}
                              className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                                isSelected ? 'bg-[#8FB560]/10' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm font-medium ${isSelected ? 'text-[#8FB560]' : 'text-foreground'}`}>
                                      {unit.unit_code}
                                    </span>
                                    {suggestion?.similarity && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        suggestion.similarity === 'exact'
                                          ? 'bg-[#8FB560]/20 text-[#8FB560]'
                                          : suggestion.similarity === 'prefix6'
                                            ? 'bg-amber-500/10 text-amber-400'
                                            : 'bg-muted text-muted-foreground'
                                      }`}>
                                        {suggestion.similarity === 'exact' ? 'Exact Match' :
                                         suggestion.similarity === 'prefix6' ? 'Likely Match' : 'Partial Match'}
                                      </span>
                                    )}
                                  </div>
                                  {unit.name && (
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                      {unit.name}
                                    </div>
                                  )}
                                </div>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-[#8FB560] flex-shrink-0 ml-2" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Group/Site Assignment (optional) */}
            {selectedUnitForLink && (
              <div className="space-y-4 pt-4 border-t border-border">
                <div>
                  <Label className="text-muted-foreground">Group Assignment (Optional)</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                    Optionally assign this unit to a group/site when linking
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Group</Label>
                  <Select
                    value={linkGroupId || '__keep_current__'}
                    onValueChange={(val) => {
                      setLinkGroupId(val === '__keep_current__' ? '' : val);
                      setLinkSiteId(''); // Reset site when group changes
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Keep current assignment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__keep_current__">Keep current assignment</SelectItem>
                      {groups
                        .filter((g) => !g.parent_group_id)
                        .map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {linkGroupId && (
                  <div className="space-y-2">
                    <Label>Site</Label>
                    <Select
                      value={linkSiteId || '__unassigned__'}
                      onValueChange={(val) => setLinkSiteId(val === '__unassigned__' ? '' : val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Unassigned (Group level only)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">Unassigned (Group level only)</SelectItem>
                        {groups
                          .filter((g) => g.parent_group_id === linkGroupId)
                          .map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLinkDrawerOpen(false);
                  setSelectedNewDevice(null);
                  setUnitSuggestions([]);
                  setSelectedUnitForLink(null);
                  setLinkGroupId('');
                  setLinkSiteId('');
                }}
                disabled={isSubmittingNewDevice}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  if (!selectedUnitForLink || !selectedNewDevice) return;
                  // Determine the primary group ID: site takes precedence over group, empty means unchanged
                  const primaryGroupId = linkSiteId || linkGroupId || undefined;
                  const success = await linkDevice(selectedNewDevice.id, selectedUnitForLink.id, { primaryGroupId });
                  if (success) {
                    setLinkDrawerOpen(false);
                    setSelectedNewDevice(null);
                    setUnitSuggestions([]);
                    setSelectedUnitForLink(null);
                    setLinkGroupId('');
                    setLinkSiteId('');
                    reloadAfterFleetMutation();
                  }
                }}
                disabled={!selectedUnitForLink || isSubmittingNewDevice}
                className="bg-[#8FB560] hover:bg-[#8FB560]/90 text-black"
              >
                {isSubmittingNewDevice ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Linking...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Link Device
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </AdminFormDrawer>

      {/* Device Picker Drawer (superadmin - link device from unit side) */}
      {editingUnit && (
        <DevicePickerDrawer
          open={devicePickerOpen}
          onClose={() => setDevicePickerOpen(false)}
          unitId={editingUnit.id}
          unitCode={editingUnit.unit_code}
          deviceType={devicePickerType}
          onLinked={handleDeviceLinked}
        />
      )}
    </div>
  );
}
